import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useAuth from "common/hooks/useAuth";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Tag } from "types/Tag";
import AlertsContext from "common/providers/alerts";
import { PiTagLight } from "react-icons/pi";
import usePark from "common/hooks/usePark";
import TagItem from "./TagItem";

function Tags() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { park } = usePark();

  const { user: currentUser } = useAuth();
  const canManageTags =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.tag?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Tag>("tags", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "name:ASC",
    cachePrefix: "Tags_all",
  });
  const { removeItem } = useItem<Tag>("tags", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/tags/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2",
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.tags")}
        subtitle={t("titleLegends.tags")}
        btnTitle={t("newItem.tag")}
        btnSubtitle={t("newItem.tagLegend")}
        Icon={PiTagLight}
        addLink="/tags/add"
        addLinkDisabled={!canManageTags}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={TagItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Tags;
